/* Ultratooth — interactive 3D implant viewer.
   Renders the real manufacturing STL geometry (the same files used for the
   digital implant library), not a stand-in model. */
(function () {
  "use strict";

  var host = document.getElementById("implant-viewer");
  if (!host || typeof THREE === "undefined") return;

  var loadEl = host.querySelector(".viewer-load");
  var buttons = Array.prototype.slice.call(document.querySelectorAll("[data-model]"));

  var renderer, scene, camera, controls, env, mesh, raf;
  var lastInteraction = 0;
  var started = false;
  var running = false;
  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---- a small procedural studio environment so titanium reads as metal --- */
  function buildEnv() {
    var c = document.createElement("canvas");
    c.width = 128; c.height = 64;
    var g = c.getContext("2d");

    var sky = g.createLinearGradient(0, 0, 0, 64);
    sky.addColorStop(0.00, "#ffffff");
    sky.addColorStop(0.42, "#a7bec2");
    sky.addColorStop(0.52, "#33484d");
    sky.addColorStop(1.00, "#080f11");
    g.fillStyle = sky;
    g.fillRect(0, 0, 128, 64);

    // key softbox + a cooler fill, which give the threads their highlights
    g.fillStyle = "rgba(255,255,255,0.98)";
    g.fillRect(12, 4, 34, 16);
    g.fillStyle = "rgba(180,240,238,0.55)";
    g.fillRect(84, 8, 26, 12);

    var tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;

    var pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    var target = pmrem.fromEquirectangular(tex);
    pmrem.dispose();
    tex.dispose();
    return target.texture;
  }

  function init() {
    renderer = new THREE.WebGLRenderer({
      antialias: !LOW_DETAIL,
      alpha: true,
      powerPreference: LOW_DETAIL ? "low-power" : "high-performance"
    });
    // capping the ratio on phones is the single biggest frame-rate win here:
    // a 3x display would otherwise shade nine times the pixels
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, LOW_DETAIL ? 1.5 : 2));
    renderer.setSize(host.clientWidth, host.clientHeight);
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.08;
    host.appendChild(renderer.domElement);

    scene = new THREE.Scene();
    env = buildEnv();
    scene.environment = env;

    camera = new THREE.PerspectiveCamera(32, host.clientWidth / host.clientHeight, 0.1, 400);
    camera.position.set(0, 2, 52);

    // direct lights on top of the environment for crisper edge definition
    var key = new THREE.DirectionalLight(0xffffff, 1.7);
    key.position.set(24, 34, 30);
    scene.add(key);

    var rim = new THREE.DirectionalLight(0x8ff0ec, 1.25);
    rim.position.set(-30, 12, -26);
    scene.add(rim);

    scene.add(new THREE.AmbientLight(0xbfd4d7, 0.22));

    controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.075;
    controls.enablePan = false;
    controls.minDistance = 26;
    controls.maxDistance = 95;
    controls.rotateSpeed = 0.72;
    controls.target.set(0, 0, 0);
    controls.addEventListener("start", function () { lastInteraction = performance.now(); });
    controls.addEventListener("change", function () { lastInteraction = performance.now(); });

    window.addEventListener("resize", resize);

    // only render while the canvas is actually on screen and the tab is active
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (entries) {
        entries.some(function (e) { return e.isIntersecting; }) ? startLoop() : stopLoop();
      }, { threshold: 0.01 }).observe(host);
    }
    document.addEventListener("visibilitychange", function () {
      document.hidden ? stopLoop() : startLoop();
    });

    startLoop();
  }

  function resize() {
    if (!renderer) return;
    var w = host.clientWidth, h = host.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  }

  function animate() {
    if (!running) return;
    raf = requestAnimationFrame(animate);
    // resume a slow turntable once the pointer has been idle for a moment
    if (mesh && !REDUCED_MOTION && performance.now() - lastInteraction > 2600) {
      mesh.rotation.y += 0.0034;
    }
    controls.update();
    renderer.render(scene, camera);
  }

  // Scrolled out of view or tab hidden: stop rendering entirely rather than
  // burning frames on an invisible canvas — on a phone that is battery.
  function startLoop() {
    if (running || !renderer) return;
    running = true;
    animate();
  }
  function stopLoop() {
    running = false;
    if (raf) cancelAnimationFrame(raf);
  }

  /* Phones and tablets get the decimated level: roughly 40% fewer triangles to
     transfer and to light, at a surface deviation far below one rendered pixel
     at that size. */
  var LOW_DETAIL =
    window.matchMedia &&
    window.matchMedia("(max-width: 900px), (pointer: coarse)").matches;

  function variantOf(article) {
    return LOW_DETAIL && window.UT_MODEL_URLS && window.UT_MODEL_URLS[article + "-lo"]
      ? article + "-lo"
      : article;
  }

  /* Models ship as indexed geometry with int16-quantised positions — about a
     quarter of the bytes of raw triangle soup. Rebuilt here, then expanded to
     non-indexed so computeVertexNormals gives the flat, machined facets the
     original STL had rather than smoothed ones. */
  function decodeModel(d) {
    var bin = atob(d.b);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);

    var n = d.n;
    var q = new Uint16Array(bytes.buffer, 0, n * 3);
    var pos = new Float32Array(n * 3);
    var ox = d.o[0], oy = d.o[1], oz = d.o[2], s = d.s;
    for (var v = 0; v < n; v++) {
      pos[v * 3]     = ox + q[v * 3]     * s;
      pos[v * 3 + 1] = oy + q[v * 3 + 1] * s;
      pos[v * 3 + 2] = oz + q[v * 3 + 2] * s;
    }
    var idx = d.w
      ? new Uint32Array(bytes.buffer, d.p, d.f * 3)
      : new Uint16Array(bytes.buffer, d.p, d.f * 3);

    var indexed = new THREE.BufferGeometry();
    indexed.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    indexed.setIndex(new THREE.BufferAttribute(idx, 1));

    var geometry = indexed.toNonIndexed();
    indexed.dispose();
    geometry.computeVertexNormals();
    return geometry;
  }

  function fetchGeometry(article, onDone, onFail) {
    var urls = window.UT_MODEL_URLS;
    if (!urls) {
      new THREE.STLLoader().load("assets/models/" + article + ".stl", onDone, null, onFail);
      return;
    }
    var key = variantOf(article);
    var store = (window.UT_MODEL_DATA = window.UT_MODEL_DATA || {});
    if (store[key]) { onDone(decodeModel(store[key])); return; }
    if (!urls[key]) { onFail(); return; }

    var s = document.createElement("script");
    s.src = urls[key];
    s.onload = function () {
      if (store[key]) onDone(decodeModel(store[key]));
      else onFail();
    };
    s.onerror = onFail;
    document.head.appendChild(s);
  }

  function load(article) {
    if (loadEl) loadEl.classList.remove("gone");

    fetchGeometry(
      article,
      function (geometry) {
        if (mesh) {
          scene.remove(mesh);
          mesh.geometry.dispose();
          mesh.material.dispose();
        }

        geometry.center();
        geometry.computeBoundingSphere();

        var material = new THREE.MeshStandardMaterial({
          color: 0xd8e1e3,
          metalness: 0.94,
          roughness: 0.29,
          envMap: env,
          envMapIntensity: 1.15
        });

        mesh = new THREE.Mesh(geometry, material);
        // the authored long axis is Y; tip it forward slightly so the
        // expansion pods at the apex stay visible in the default view
        mesh.rotation.x = -0.16;
        scene.add(mesh);

        // frame the implant regardless of which length is selected
        var r = geometry.boundingSphere.radius;
        var dist = r / Math.sin((camera.fov * Math.PI) / 180 / 2);
        controls.minDistance = dist * 0.62;
        controls.maxDistance = dist * 2.4;
        camera.position.set(0, r * 0.12, dist * 1.06);
        controls.target.set(0, 0, 0);
        controls.update();

        if (loadEl) loadEl.classList.add("gone");
      },
      function () {
        if (loadEl) loadEl.textContent = "Model unavailable";
      }
    );
  }

  function start() {
    if (started) return;
    started = true;
    try {
      init();
      var initial = document.querySelector('[data-model][aria-pressed="true"]') || buttons[0];
      load(initial ? initial.getAttribute("data-model") : "3913UT");
    } catch (err) {
      if (loadEl) loadEl.textContent = "3D view not supported";
    }
  }

  buttons.forEach(function (btn) {
    btn.addEventListener("click", function () {
      buttons.forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
      btn.setAttribute("aria-pressed", "true");
      lastInteraction = performance.now();
      if (started) load(btn.getAttribute("data-model"));
    });
  });

  // only pull ~2 MB of geometry once the viewer is actually on screen
  if ("IntersectionObserver" in window) {
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (e) { return e.isIntersecting; })) {
        io.disconnect();
        start();
      }
    }, { rootMargin: "200px" });
    io.observe(host);
  } else {
    start();
  }
})();
